/**
 * Страница одной задачи.
 *
 * Правки применяются сразу и откатываются при отказе — как в списке: страница
 * задачи это рабочий стол, и поле, которое меняется через полсекунды, читается
 * как «не сработало».
 *
 * Вместе с задачей приезжают проект, его колонки и участники: без них в
 * мета-панели нет ни имени статуса, ни его цвета, ни из кого выбирать
 * исполнителя.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { moveIssue } from '@/features/issues/api'
import { rememberIssue } from '@/features/issues/recents'
import {
  getProject,
  listColumns,
  listMembers,
  type Project,
  type ProjectColumn,
  type ProjectMember,
} from '@/features/projects/api'

import { useAuthStore } from '@/features/auth/store'

import {
  createAttachment,
  deleteAttachment,
  listAttachments,
  uploadIssueFile,
  type Attachment,
} from './attachments'
import {
  createLabel,
  getIssueByKey,
  listLabels,
  setLabels,
  setWatcher,
  updateIssue,
  type Issue,
  type IssuePatch,
  type Label,
} from './api'

export const useIssueStore = defineStore('issue', () => {
  const auth = useAuthStore()

  const issue = ref<Issue | null>(null)
  const project = ref<Project | null>(null)
  const columns = ref<ProjectColumn[]>([])
  const members = ref<ProjectMember[]>([])
  /** Метки заводятся в проекте: на задачу вешают уже готовые */
  const labels = ref<Label[]>([])
  /** Вложения приезжают вместе с задачей: их счётчик стоит в мета-панели */
  const attachments = ref<Attachment[]>([])

  const isLoading = ref(false)
  const error = ref<unknown>(null)
  /** Задача не нашлась по ключу: это 404, а не отказ сервиса */
  const isMissing = ref(false)

  const column = computed(
    () => columns.value.find((entry) => entry.id === issue.value?.columnId) ?? null,
  )

  // Перечитываем всегда, даже если та же задача уже в сторе: стор один на
  // приложение, и вернувшийся на страницу человек ждёт свежий статус, а не тот,
  // что был до его же правки в списке.
  const open = async (key: string) => {
    isLoading.value = true
    error.value = null
    isMissing.value = false
    issue.value = null

    try {
      const found = await getIssueByKey(key)

      if (!found) {
        isMissing.value = true

        return
      }

      issue.value = found

      // Открытая задача попадает в «Недавние» палитры (DEV-2049): и страница,
      // и peek проходят через этот стор — точка одна.
      rememberIssue({ key: found.key, title: found.title })

      // Проект и его справочники — вторым заходом и мягко: без них страница
      // беднее, но задача уже на экране.
      const [foundProject, projectColumns, projectMembers, projectLabels, issueFiles] =
        await Promise.all([
          getProject(found.projectId).catch(() => null),
          listColumns(found.projectId).catch(() => []),
          listMembers(found.projectId).catch(() => []),
          listLabels(found.projectId).catch(() => []),
          listAttachments(found.id).catch(() => []),
        ])

      project.value = foundProject
      columns.value = projectColumns
      members.value = projectMembers
      labels.value = projectLabels
      attachments.value = issueFiles
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /** Правка на месте с откатом: сервер отказал — поле возвращается как было */
  const patch = async (change: Partial<Issue>, send: () => Promise<void>) => {
    const target = issue.value

    if (!target) return

    const saved = { ...target }

    Object.assign(target, change)

    try {
      await send()
    } catch (failure) {
      Object.assign(target, saved)

      throw failure
    }
  }

  const setTitle = (title: string) => {
    if (!issue.value || title.trim() === issue.value.title) return Promise.resolve()

    return patch({ title: title.trim() }, () =>
      updateIssue(issue.value!.id, { title: title.trim() }),
    )
  }

  const setField = (change: Partial<Issue>, body: IssuePatch) =>
    patch(change, () => updateIssue(issue.value!.id, body))

  /**
   * Исполнитель меняется на месте, поэтому вместе с именем берём и фото: ответ
   * сервера в карточку не подставляется, и без этого у нового исполнителя до
   * перезагрузки оставался кружок с инициалами.
   */
  const setAssignee = (assigneeId: string, assigneeName: string, assigneeAvatar = '') =>
    setField({ assigneeId, assigneeName, assigneeAvatar }, { assigneeId })

  const setPriority = (priority: Issue['priority']) =>
    setField({ priority }, { priority: priority || undefined })

  const setDueDate = (dueDate: string) => setField({ dueDate }, { dueDate })

  /** Статус — это колонка доски: другого хранилища статуса в трекере нет */
  const setColumn = (columnId: string) =>
    patch({ columnId }, () => moveIssue(issue.value!.id, columnId))

  /**
   * Смена родителя. Пустое значение — «без родителя», и его сервис ждёт
   * пустой строкой: поле объявлено `optional string` с проверкой на uuid, и
   * отправить туда «ничего» иначе нечем. Ключ родителя после правки приедет с
   * задачей — придумывать его на клиенте не из чего.
   */
  const setParent = (parentId: string) =>
    patch({ parentId, parentKey: '' }, () => updateIssue(issue.value!.id, { parentId }))

  const setDescription = (descriptionMd: string, mentionedUserIds: string[]) =>
    setField({ descriptionMd }, { descriptionMd, mentionedUserIds })

  /**
   * Метки ставятся списком целиком — отдельных «добавить» и «снять» у сервиса
   * нет. Экран держит на задаче те же объекты, что в справочнике проекта: имя
   * и цвет метки берутся оттуда, а на задаче лежат только её метки.
   */
  const setIssueLabels = (labelIds: string[]) =>
    patch({ labels: labels.value.filter((label) => labelIds.includes(label.id)) }, () =>
      setLabels(issue.value!.id, labelIds),
    )

  /**
   * Метка на лету: сначала заводим её в проекте, потом вешаем. Ждать, пока
   * кто-то заведёт «backend» в настройках, чтобы отметить им задачу, —
   * лишний поход на другой экран посреди работы.
   */
  const addLabel = async (name: string, color: string) => {
    const created = await createLabel(project.value!.id, name.trim(), color)

    if (!created) return

    labels.value = [...labels.value, created]

    await setIssueLabels([...(issue.value?.labels ?? []).map((label) => label.id), created.id])
  }

  const isWatching = computed(() =>
    Boolean(auth.user && issue.value?.watcherIds.includes(auth.user.id)),
  )

  /**
   * Подписка на задачу. Список наблюдателей возвращает сервис — своими руками
   * его не собираем: подписка приходит ещё и от упоминания в описании, и
   * угадывать здесь означало бы разойтись с сервером на ровном месте.
   */
  const toggleWatching = async () => {
    const target = issue.value

    if (!target) return

    target.watcherIds = await setWatcher(target.id, !isWatching.value)
  }

  /**
   * Наблюдение за задачу другого человека: в карточке наблюдателей набирают из
   * участников проекта, а не ждут, пока каждый подпишется сам.
   */
  const setWatching = async (userId: string, watching: boolean) => {
    const target = issue.value

    if (!target) return

    target.watcherIds = await setWatcher(target.id, watching, userId)
  }

  /**
   * Вложение: файл сначала уезжает в хранилище, потом его ключ прикрепляется к
   * задаче. Порядок обратный — привязать несуществующий файл — оставил бы в
   * карточке плитку, которая не открывается.
   */
  const attachFile = async (file: File) => {
    const target = issue.value

    if (!target) return

    const fileId = await uploadIssueFile(target.id, file)
    const created = await createAttachment(target.id, fileId, file)

    if (created) attachments.value = [...attachments.value, created]
  }

  /**
   * Удаление без предугадывания: файл уходит из хранилища насовсем, и плитка,
   * которая исчезла и вернулась из-за отказа, читается хуже, чем полсекунды
   * ожидания.
   */
  const removeAttachment = async (id: string) => {
    await deleteAttachment(id)

    attachments.value = attachments.value.filter((attachment) => attachment.id !== id)
  }

  return {
    issue,
    project,
    columns,
    members,
    labels,
    attachments,
    isLoading,
    error,
    isMissing,
    column,
    isWatching,
    open,
    setTitle,
    setAssignee,
    setPriority,
    setDueDate,
    setColumn,
    setParent,
    setDescription,
    setIssueLabels,
    addLabel,
    toggleWatching,
    setWatching,
    attachFile,
    removeAttachment,
  }
})
